import React, { useMemo, useState, useEffect } from "react";
import NewNavbar from "../components/NewNavbar";
import Footer from "../components/Footer";
import BlogCard from "../components/BlogCard";
import blogImageOne from "../assets/blog-image-1.png";
import blogImageTwo from "../assets/blog-image-2.png";
import blogImageThree from "../assets/blog-image-3.png";
import SEO from "../components/SEO.js";
import { ReactComponent as ModulesIcon } from "./../assets/svgs/modules-icon.svg";
import { usePublicBlogsListQuery } from "../store/generated.ts";

function formatBlogDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleDateString("en-GB", { day: "2-digit", month: "long" })
    .replace(/^0/, "");
}

const fallbackImages = [blogImageOne, blogImageTwo, blogImageThree];

const EvenessBlog = () => {
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [allResults, setAllResults] = useState([]);

  const { data, isLoading, isFetching, isError } = usePublicBlogsListQuery({
    page,
    pageSize,
  });

  useEffect(() => {
    setPage(1);
    setAllResults([]);
  }, [pageSize]);

  useEffect(() => {
    const incoming = data?.results ?? [];
    if (!incoming.length) return;

    setAllResults((prev) => {
      const seen = new Set(prev.map((x) => x?.id));
      const merged = [...prev];

      for (const item of incoming) {
        if (item?.id == null) continue;
        if (!seen.has(item.id)) {
          merged.push(item);
          seen.add(item.id);
        }
      }
      return merged;
    });
  }, [data?.results]);

  const blogs = useMemo(() => {
    return (allResults ?? []).map((b, idx) => ({
      id: b?.id,
      slug: b?.slug ?? "",
      title: b?.title ?? "",
      content: b?.preview ?? b?.content_md ?? "",
      author: b?.author_name ?? "",
      date: formatBlogDate(b?.published_at),
      imageSrc: b?.hero_image_url || fallbackImages[idx % fallbackImages.length],
      href: b?.slug ? `/blog/${b.slug}` : "#",
    }));
  }, [allResults]);

  const canViewMore = Boolean(data?.next);

  const showEmptyState =
    !isLoading && !isFetching && !isError && (blogs?.length ?? 0) === 0;

  return (
    <div className="min-h-screen bg-[#F4F4F4] text-black">
      <SEO
        title="Eveness Journal | AI Fashion, Styling & Shopping Tips"
        description="Read the Eveness Journal for AI fashion insights, styling advice, shopping tips, wardrobe guidance, and updates from the future of personalized fashion."
        path="/blog"
        type="blog"
      />
      {/* Navbar */}
      <NewNavbar />
      <main className="mx-auto mt-4 w-full max-w-[1080px] px-3 pb-16">
        <section className="rounded-[5px] border border-[#00000033] bg-white px-4 py-5 md:px-5">
          <header className="border-b border-[#0000000D] pb-6">
            <p className="mb-3 font-spaceMono text-[8px] md:text-[9px] font-bold uppercase tracking-[0.24em] text-[#71717B] flex flex-row gap-2">
              <ModulesIcon />
              <p> Core_Modules // Loaded</p>
            </p>
            <h1 className="font-montserrat text-[38px] font-semibold leading-[48px] tracking-[-1.2px] text-black md:text-[48px]">
              The Eveness Journal
            </h1>
            <p className="mt-5 max-w-[390px] font-spaceMono text-[11px] md:text-[14px] leading-[22.75px] tracking-[0px] text-[#52525C] font-normal">
              Where luxury fashion meets technology. Discover insights, updates,
              and trends from the future of personalized styling.
            </p>
          </header>

        {/* Empty State */}
          {showEmptyState && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="mb-6 h-24 w-24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3L1 21H23L12 3Z"
                  stroke="#EF4444"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M12 9V14"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="17" r="1" fill="#EF4444" />
              </svg>

              <h3 className="font-helvetica text-[28px] font-bold text-black">
                No blogs found!
              </h3>
            </div>
          )}

        {/* Grid */}
          {!showEmptyState && (
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              {(isLoading && page === 1
                ? Array.from({ length: 4 })
                : blogs
              ).map((j, i) => (
                <div key={j?.id ?? i} className="h-full">
                  {isLoading && page === 1 ? (
                    <div className="h-[430px] rounded-[5px] border border-[#E5E5E5] bg-[#F7F7F7]" />
                  ) : (
                    <BlogCard
                      title={j.title}
                      content={j.content}
                      author={j.author}
                      date={j.date}
                      imageSrc={j.imageSrc}
                      href={j.href}
                    />
                  )}
                </div>
              )
              )}
            </div>
          )}

          {/* Errors */}
          {isError && (
            <div className="mt-6 text-center font-helvetica text-sm text-red-500">
              Failed to load blogs. Please try again.
            </div>
          )}

          {canViewMore && (
            <div className="pt-[30px] text-center">
              <button
                type="button"
                onClick={() => {
                  if (isFetching) return;
                  setPage((p) => p + 1);
                }}
                className="inline-flex h-[42px] items-center justify-center rounded-[5px] bg-[#202020] px-6 font-helvetica text-[14px] font-medium text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isFetching}
              >
                {isFetching ? "Loading..." : "View More"}
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EvenessBlog;
